/**
 * useDevise — détection automatique de devise selon le pays (ipapi.co).
 *
 * - Premier passage : appelle ipapi.co/json/ pour déduire `pays` + `devise`.
 * - Stockage dans localStorage : ne refait pas l'appel aux visites suivantes.
 * - L'utilisateur peut surcharger via SwitchDevise (persisté en local).
 */
import { useCallback, useEffect, useState } from "react";
import { PAYS_DEVISE, TARIFS, DEVISES_LIST, formatPrix } from "../config/devises";

const STORAGE_KEY = "mm_devise";
const PAYS_KEY = "mm_pays";

export function useDevise() {
  const initial = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const initialPays = typeof window !== "undefined" ? localStorage.getItem(PAYS_KEY) : null;
  const [devise, setDeviseState] = useState(initial && TARIFS[initial] ? initial : "EUR");
  const [pays, setPays] = useState(initialPays || null);
  const [paysCode, setPaysCode] = useState(null);
  const [loading, setLoading] = useState(!initial);
  const [autoDetected, setAutoDetected] = useState(!initial);

  useEffect(() => {
    if (initial && TARIFS[initial]) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch("https://ipapi.co/json/")
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("ipapi unreachable")))
      .then((data) => {
        if (cancelled) return;
        const code = (data?.country_code || "").toUpperCase();
        const name = data?.country_name || null;
        const d = PAYS_DEVISE[code] || "EUR";
        setPays(name);
        setPaysCode(code);
        setDeviseState(d);
        try {
          if (name) localStorage.setItem(PAYS_KEY, name);
        } catch { /* quota */ }
      })
      .catch(() => {
        if (!cancelled) setDeviseState("EUR");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [initial]);

  const changerDevise = useCallback((d) => {
    if (!TARIFS[d]) return;
    setDeviseState(d);
    setAutoDetected(false);
    try { localStorage.setItem(STORAGE_KEY, d); } catch { /* quota */ }
  }, []);

  const prix = useCallback(
    (plan, cycle = "mensuel") => TARIFS[devise]?.plans?.[plan]?.[cycle] ?? 0,
    [devise],
  );
  const prixAddon = useCallback(
    (id, cycle = "mensuel") => TARIFS[devise]?.addons?.[id]?.[cycle] ?? 0,
    [devise],
  );
  const afficher = useCallback((montant) => formatPrix(montant, devise), [devise]);

  return {
    devise,
    pays,
    paysCode,
    loading,
    autoDetected,
    symbole: TARIFS[devise]?.symbole || "€",
    drapeau: TARIFS[devise]?.drapeau || "🇪🇺",
    nom: TARIFS[devise]?.nom || "Euro",
    changerDevise,
    prix,
    prixAddon,
    afficher,
    devises: DEVISES_LIST,
  };
}

export default useDevise;
